/**
 * Espace de la plateforme web, choisi UNE fois au démarrage (index.js / App.js) :
 *
 * - `league` : espace de la Ligue (MySquash : clubs, saisons, affiliations, interclubs…, voir src/league/) ;
 * - `club`   : espace du club (l'ERP : back-office du staff, pages publiques, TV, OBS) — tout le reste.
 *
 * Ordre de résolution :
 * 1. `?space=league` / `?space=club` dans l'URL : vaut pour ce chargement ; `league` est mémorisé pour l'onglet
 *    (sessionStorage), pour que la navigation et les rechargements restent dans la Ligue ; `club` l'oublie.
 * 2. Le choix mémorisé de l'onglet (`league`).
 * 3. L'hôte : `<sous-domaine Ligue>.<domaine racine>` (ex. `lfs.mysquash.be`) ou `<sous-domaine Ligue>.localhost`
 *    (ex. `lfs.localhost:3000` en développement) → `league` ; tout autre hôte → `club`.
 *
 * Le domaine racine n'est pas encore acquis : il se règle au build avec `REACT_APP_ROOT_DOMAIN` (ex. `mysquash.be`) ;
 * le sous-domaine de la Ligue avec `REACT_APP_LEAGUE_SUBDOMAIN` (défaut `lfs`). Sans domaine racine, seuls
 * `?space=league` et `lfs.localhost` ouvrent la Ligue (ils marchent partout, y compris sur l'hôte de prod actuel).
 *
 * Module pur (aucune API, aucun import) : testé dans platformSpace.test.js.
 */

export const LEAGUE_SPACE = 'league';
export const CLUB_SPACE = 'club';

/** Clé sessionStorage du choix `?space=league` (par onglet). */
export const SPACE_SESSION_KEY = 'courtly.platformSpace';

const DEFAULT_LEAGUE_SUBDOMAIN = 'lfs';

/** Nom d'hôte normalisé : minuscules, sans port ni point final. */
function normalizeHost(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/:\d+$/, '')
    .replace(/\.$/, '');
}

/** Domaine racine de la plateforme (`REACT_APP_ROOT_DOMAIN`), '' s'il n'est pas configuré. */
export function getRootDomain(env = process.env) {
  return normalizeHost(env.REACT_APP_ROOT_DOMAIN).replace(/^\.+/, '');
}

/** Sous-domaine de la Ligue (`REACT_APP_LEAGUE_SUBDOMAIN`, défaut `lfs`). */
export function getLeagueSubdomain(env = process.env) {
  return normalizeHost(env.REACT_APP_LEAGUE_SUBDOMAIN) || DEFAULT_LEAGUE_SUBDOMAIN;
}

/** Vrai si l'hôte est celui de la Ligue : `<sous-domaine>.<racine>` ou `<sous-domaine>.localhost`. */
export function isLeagueHost(hostname, { rootDomain = getRootDomain(), leagueSubdomain = getLeagueSubdomain() } = {}) {
  const host = normalizeHost(hostname);
  if (!host || !leagueSubdomain) return false;
  if (host === `${leagueSubdomain}.localhost`) return true;
  return Boolean(rootDomain) && host === `${leagueSubdomain}.${rootDomain}`;
}

/** `league` | `club` lu dans `?space=`, sinon ''. */
function spaceFromSearch(search) {
  try {
    const value = String(new URLSearchParams(search || '').get('space') || '').trim().toLowerCase();
    return value === LEAGUE_SPACE || value === CLUB_SPACE ? value : '';
  } catch {
    return '';
  }
}

function defaultStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function defaultLocation() {
  try {
    return window.location;
  } catch {
    return null;
  }
}

function storageGet(storage, key) {
  try {
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
}

function storageSet(storage, key, value) {
  try {
    if (!storage) return;
    if (value) storage.setItem(key, value);
    else storage.removeItem(key);
  } catch {
    /* stockage indisponible : le choix ne vaut que pour ce chargement */
  }
}

/**
 * Espace courant : `league` ou `club`. Les paramètres servent aux tests ; par défaut, `window.location`,
 * `window.sessionStorage` et les variables `REACT_APP_*` du build.
 */
export function getPlatformSpace({
  hostname,
  search,
  storage = defaultStorage(),
  rootDomain = getRootDomain(),
  leagueSubdomain = getLeagueSubdomain(),
} = {}) {
  const location = hostname == null || search == null ? defaultLocation() : null;
  const host = hostname ?? location?.hostname ?? '';
  const query = search ?? location?.search ?? '';

  const fromQuery = spaceFromSearch(query);
  if (fromQuery) {
    storageSet(storage, SPACE_SESSION_KEY, fromQuery === LEAGUE_SPACE ? LEAGUE_SPACE : null);
    return fromQuery;
  }
  if (storageGet(storage, SPACE_SESSION_KEY) === LEAGUE_SPACE) return LEAGUE_SPACE;
  return isLeagueHost(host, { rootDomain, leagueSubdomain }) ? LEAGUE_SPACE : CLUB_SPACE;
}

/** Raccourci : vrai dans l'espace de la Ligue. */
export const isLeagueSpace = (options) => getPlatformSpace(options) === LEAGUE_SPACE;

// ── Liens entre espaces (sélecteur d'espace en haut à droite) ──

function locationParts(location) {
  const loc = location || defaultLocation() || {};
  return { protocol: loc.protocol || 'https:', hostname: normalizeHost(loc.hostname), port: loc.port || '', origin: loc.origin || '' };
}

const withPort = (host, port) => (port ? `${host}:${port}` : host);

function withQuery(path, params) {
  const [base, hash] = String(path || '/').split('#');
  const query = new URLSearchParams(params).toString();
  return `${base}${base.includes('?') ? '&' : '?'}${query}${hash != null ? `#${hash}` : ''}`;
}

/**
 * URL de l'espace Ligue : `lfs.<racine>` si le domaine racine est réglé, sinon même origine avec `?space=league`.
 * Les options servent aux tests.
 */
export function leagueSpaceUrl(path = '/', { location, rootDomain = getRootDomain(), leagueSubdomain = getLeagueSubdomain() } = {}) {
  const { protocol, port, origin } = locationParts(location);
  if (rootDomain) return `${protocol}//${withPort(`${leagueSubdomain}.${rootDomain}`, port)}${path}`;
  return `${origin}${withQuery(path, { space: LEAGUE_SPACE })}`;
}

/**
 * URL de l'ERP d'un club : `<slug>.<racine>` si le domaine racine est réglé ; sinon même origine avec
 * `?space=club&club=<slug>` (depuis `lfs.localhost`, l'origine `localhost` : l'hôte de la Ligue resterait la Ligue).
 */
export function clubSpaceUrl(slug, path = '/app', { location, rootDomain = getRootDomain(), leagueSubdomain = getLeagueSubdomain() } = {}) {
  const { protocol, hostname, port, origin } = locationParts(location);
  const club = String(slug || '').trim().toLowerCase();
  if (rootDomain && club) return `${protocol}//${withPort(`${club}.${rootDomain}`, port)}${path}`;
  const base = hostname === `${leagueSubdomain}.localhost` ? `${protocol}//${withPort('localhost', port)}` : origin;
  return `${base}${withQuery(path, club ? { space: CLUB_SPACE, club } : { space: CLUB_SPACE })}`;
}
